// Builds a clean thermal-style order ticket and prints it via a hidden iframe.
// Works with any connected/default printer; if the owner enables browser
// "kiosk/silent print", auto-triggered tickets print without a dialog.

const money = (n) => "\u20B9" + Number(n || 0).toFixed(2);

export function orderReceiptHtml(order, restaurant) {
  const name = restaurant?.name || order?.restaurant_name || "Restaurant";
  const id = String(order?.order_id || "").slice(-6).toUpperCase();
  const when = new Date(order?.created_at || Date.now()).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  const pay = order?.payment_method === "razorpay" ? "PAID ONLINE" : "CASH ON DELIVERY";
  const rows = (order?.items || [])
    .map((i) => `<tr><td>${i.qty} &times; ${escapeHtml(i.name)}</td><td class="r">${money(i.line_total ?? i.price * i.qty)}</td></tr>`)
    .join("");
  const line = (label, val) => `<tr><td>${label}</td><td class="r">${val}</td></tr>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>Ticket ${id}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, 'Segoe UI', Roboto, monospace, sans-serif; width: 280px; margin: 0 auto; padding: 10px; color: #000; }
    h1 { font-size: 16px; text-align: center; margin: 0 0 2px; }
    .muted { font-size: 11px; text-align: center; color: #333; margin: 0; }
    .badge { text-align: center; font-weight: 800; border: 2px solid #000; border-radius: 6px; padding: 4px; margin: 8px 0; font-size: 12px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    td { padding: 2px 0; vertical-align: top; }
    .r { text-align: right; white-space: nowrap; padding-left: 8px; }
    .sep { border-top: 1px dashed #000; margin: 6px 0; }
    .total td { font-weight: 800; font-size: 14px; border-top: 1px solid #000; padding-top: 4px; }
    .addr { font-size: 11px; margin: 6px 0; }
    .foot { text-align: center; font-size: 10px; margin-top: 8px; color: #333; }
  </style></head><body>
    <h1>${escapeHtml(name)}</h1>
    <p class="muted">Order #${id} &middot; ${when}</p>
    <div class="badge">${escapeHtml((order?.status || "").toUpperCase() || "NEW")}${order?.auto_accepted ? " &middot; AUTO" : ""}</div>
    <p class="addr"><strong>${escapeHtml(order?.customer_name || "Customer")}</strong><br/>${escapeHtml(order?.delivery_address || "")}${order?.customer_phone ? "<br/>" + escapeHtml(order.customer_phone) : ""}</p>
    ${order?.notes ? `<p class="addr">Note: ${escapeHtml(order.notes)}</p>` : ""}
    <div class="sep"></div>
    <table>${rows}</table>
    <div class="sep"></div>
    <table>
      ${line("Subtotal", money(order?.subtotal))}
      ${order?.packaging_fee > 0 ? line("Packaging", money(order.packaging_fee)) : ""}
      ${line("Delivery", money(order?.delivery_fee))}
      ${order?.tax ? line("Tax", money(order.tax)) : ""}
      ${order?.discount > 0 ? line("Discount", "-" + money(order.discount)) : ""}
      <tr class="total"><td>TOTAL</td><td class="r">${money(order?.total)}</td></tr>
    </table>
    <div class="badge" style="margin-top:8px">${pay}</div>
    <p class="foot">KohimaTaxi &amp; Delivery</p>
  </body></html>`;
}

function escapeHtml(s) {
  return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

export function printOrderReceipt(order, restaurant) {
  try {
    const html = orderReceiptHtml(order, restaurant);
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    const done = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ } setTimeout(() => iframe.remove(), 1500); };
    if (iframe.contentWindow.document.readyState === "complete") setTimeout(done, 150);
    else iframe.onload = () => setTimeout(done, 150);
    return true;
  } catch {
    return false;
  }
}


// Kitchen Order Ticket (KOT) — a kitchen-focused ticket with large item text and
// NO prices (the kitchen only needs what to cook). Printed automatically when an
// order is accepted, and reprintable from the board.
export function kotHtml(order, restaurant) {
  const name = restaurant?.name || order?.restaurant_name || "Restaurant";
  const id = String(order?.order_id || "").slice(-6).toUpperCase();
  const when = new Date(order?.created_at || Date.now()).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  const type = (order?.order_type === "pickup") ? "SELF PICK-UP" : "DELIVERY";
  const prep = order?.prep_min ? `READY IN ~${order.prep_min} MIN` : "";
  const rows = (order?.items || [])
    .map((i) => `<tr><td class="q">${i.qty}&times;</td><td class="n">${escapeHtml(i.name)}${i.veg === false ? ' <span class="nv">NON-VEG</span>' : ""}</td></tr>`)
    .join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>KOT ${id}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, 'Segoe UI', Roboto, monospace, sans-serif; width: 280px; margin: 0 auto; padding: 10px; color: #000; }
    h1 { font-size: 15px; text-align: center; margin: 0; letter-spacing: 1px; }
    .tkt { text-align: center; font-weight: 800; font-size: 13px; margin: 2px 0 4px; }
    .muted { font-size: 11px; text-align: center; color: #333; margin: 0; }
    .badge { text-align: center; font-weight: 800; border: 2px solid #000; border-radius: 6px; padding: 5px; margin: 8px 0; font-size: 14px; }
    table { width: 100%; border-collapse: collapse; }
    td { padding: 4px 0; vertical-align: top; border-bottom: 1px dashed #bbb; }
    .q { font-size: 18px; font-weight: 800; white-space: nowrap; padding-right: 8px; width: 1%; }
    .n { font-size: 16px; font-weight: 700; }
    .nv { font-size: 9px; border: 1px solid #000; padding: 0 3px; border-radius: 3px; vertical-align: middle; }
    .note { font-size: 13px; font-weight: 700; border: 1px solid #000; border-radius: 6px; padding: 6px; margin-top: 8px; }
    .foot { text-align: center; font-size: 10px; margin-top: 8px; color: #333; }
  </style></head><body>
    <h1>${escapeHtml(name)}</h1>
    <p class="tkt">KITCHEN ORDER TICKET</p>
    <p class="muted">#${id} &middot; ${when}</p>
    <div class="badge">${type}${prep ? " &middot; " + prep : ""}</div>
    <table>${rows}</table>
    ${order?.notes ? `<p class="note">NOTE: ${escapeHtml(order.notes)}</p>` : ""}
    <p class="foot">KohimaTaxi &amp; Delivery</p>
  </body></html>`;
}

export function printKot(order, restaurant) {
  try {
    const html = kotHtml(order, restaurant);
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    document.body.appendChild(iframe);
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();
    const done = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ } setTimeout(() => iframe.remove(), 1500); };
    if (iframe.contentWindow.document.readyState === "complete") setTimeout(done, 150);
    else iframe.onload = () => setTimeout(done, 150);
    return true;
  } catch {
    return false;
  }
}
